import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaEye, FaEyeSlash, FaPlus, FaSearch, FaEdit } from 'react-icons/fa';
import { 
  TextField, 
  Button, 
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  InputAdornment
} from '@mui/material';
import { Container, Header, HeaderLeft, Title, Content, BackButton, HeaderButton, ControlsContainer, MainContainer } from '../../common/styles';
import ConfirmationModal from '../../common/ConfirmationModal';
import Modal from '../../common/Modal';
import LoadingSpinner from '../../common/LoadingSpinner';
import { useAlert } from '../../../contexts/AlertContext';
import { organizationApi } from '../../../services/api';

const BusinessAreasConfiguration = () => {
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const [showControls, setShowControls] = useState(true);
  const [businessAreas, setBusinessAreas] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  
  const [areaModal, setAreaModal] = useState({ 
    open: false, 
    isEdit: false, 
    formData: { name: '' },
    originalId: null
  });
  
  const [deleteModal, setDeleteModal] = useState({ open: false, businessArea: null });
  const [isLoading, setIsLoading] = useState(true);

  const fetchBusinessAreas = async () => {
    try {
      setIsLoading(true);
      const data = await organizationApi.getBusinessAreas();
      setBusinessAreas(data);
    } catch (err) {
      showAlert('Failed to load business areas.', 'error', err);
      console.error('Error fetching business areas:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchBusinessAreas();
  }, []);

  const handleSaveBusinessArea = async (e) => {
    e.preventDefault();
    
    try {
      setIsLoading(true);
      
      if (areaModal.isEdit) {
        if (!areaModal.originalId) {
          showAlert('Invalid business area data for update', 'error');
          return;
        }
        
        await organizationApi.updateBusinessArea(areaModal.originalId, {
          name: areaModal.formData.name
        });
        showAlert('Business area updated successfully', 'success');
      } else {
        await organizationApi.createBusinessArea({
          name: areaModal.formData.name
        });
        showAlert('Business area added successfully', 'success');
      }
      
      await fetchBusinessAreas();
      setAreaModal({ 
        open: false, 
        isEdit: false, 
        formData: { name: '' },
        originalId: null
      });
    } catch (err) {
      const action = areaModal.isEdit ? 'update' : 'add';
      showAlert(err.message || `Failed to ${action} business area.`, 'error', err);
      console.error(`Error ${action}ing business area:`, err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteBusinessArea = (businessArea) => {
    setDeleteModal({ open: true, businessArea });
  };

  const confirmDelete = async () => {
    const areaToDelete = deleteModal.businessArea;
    setDeleteModal({ open: false, businessArea: null });
    
    if (!areaToDelete?.id) {
      showAlert('Invalid business area data', 'error');
      return;
    }

    try {
      setIsLoading(true);
      await organizationApi.deleteBusinessArea(areaToDelete.id);
      await fetchBusinessAreas();
      showAlert('Business area deleted successfully', 'success');
    } catch (err) {
      console.error('Error deleting business area:', err);
      showAlert(err.message || 'Failed to delete business area.', 'error', err);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredBusinessAreas = businessAreas.filter(area => {
    if (!searchTerm) return true;
    return area.name.toLowerCase().includes((searchTerm || '').toLowerCase());
  });

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={() => navigate('/organization')}>
            <FaArrowLeft /> Back to Organization
          </BackButton>
          <Title>Business Areas Configuration</Title>
        </HeaderLeft>
        <HeaderButton onClick={() => setShowControls(!showControls)}>
          {showControls ? <FaEyeSlash /> : <FaEye />}
          {showControls ? 'Hide Panels' : 'Show Panels'}
        </HeaderButton>
      </Header>

      <Content>
        <div style={{ flex: 1 }}>
          <ControlsContainer $show={showControls} style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between' }}>
            <TextField
              placeholder="Search Business Area"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <FaSearch style={{ color: '#6B7280' }} />
                    </InputAdornment>
                  ),
                }
              }}
              sx={{
                minWidth: '300px',
              }}
            />
            <HeaderButton onClick={() => setAreaModal({
              open: true,
              isEdit: false,
              formData: { name: '' },
              originalId: null
            })}>
              <FaPlus /> Add Business Area
            </HeaderButton>
          </ControlsContainer>

          <MainContainer $show={showControls}>
            <div style={{ 
              width: '100%',
              height: '100%',
              overflowY: 'auto',
              padding: '24px'
            }}>
              <Grid 
                container 
                spacing={3}
                sx={{
                  width: '100%',
                  margin: '0 auto',
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, 1fr)',
                    md: 'repeat(3, 1fr)',
                    lg: 'repeat(4, 1fr)',
                    xl: 'repeat(4, 1fr)'
                  },
                  gap: '24px',
                  '& .MuiGrid-item': {
                    padding: '0',
                    width: '100%'
                  }
                }}
              >
                {filteredBusinessAreas.map((businessArea) => (
                  <Grid 
                    item
                    key={businessArea.id}
                  >
                    <Card 
                      sx={{ 
                        width: '100%',
                        minHeight: '120px',
                        display: 'flex',
                        flexDirection: 'column',
                        transition: 'all 0.2s',
                        border: '1px solid #e0e0e0',
                        boxShadow: 'none',
                        borderRadius: '4px',
                        backgroundColor: '#f8f9fa',
                        '&:hover': {
                          borderColor: '#bdbdbd',
                          backgroundColor: '#f1f3f5',
                        }
                      }}
                    >
                      <CardContent sx={{ 
                        display: 'flex', 
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        padding: '16px !important',
                        flexGrow: 1,
                      }}>
                        <Typography 
                          variant="h6" 
                          sx={{ 
                            fontWeight: 500,
                            color: '#212529',
                          }}
                        >
                          {businessArea.name}
                        </Typography>

                        <Box sx={{ 
                          display: 'flex',
                          justifyContent: 'flex-end',
                          gap: '8px',
                          marginTop: '16px'
                        }}>
                          <Button
                            onClick={() => setAreaModal({
                              open: true,
                              isEdit: true,
                              formData: { name: businessArea.name },
                              originalId: businessArea.id
                            })}
                            sx={{
                              minWidth: 'unset',
                              padding: '6px 12px',
                              fontSize: '14px',
                              color: '#0d6efd',
                              transition: 'all 0.2s ease',
                              backgroundColor: 'rgba(13, 110, 253, 0.1)',
                              textTransform: 'none',
                              '&:hover': {
                                backgroundColor: 'rgba(13, 110, 253, 0.15)',
                                color: '#0d6efd'
                              }
                            }}
                          >
                            Edit
                          </Button>

                          <Button
                            onClick={() => handleDeleteBusinessArea(businessArea)}
                            sx={{
                              minWidth: 'unset',
                              padding: '6px 12px',
                              fontSize: '14px',
                              color: '#dc3545',
                              transition: 'all 0.2s ease',
                              backgroundColor: 'rgba(220, 53, 69, 0.1)',
                              textTransform: 'none',
                              '&:hover': {
                                backgroundColor: 'rgba(220, 53, 69, 0.15)',
                                color: '#dc3545'
                              }
                            }}
                          >
                            Delete
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </div>
          </MainContainer>
        </div>
      </Content>

      <Modal
        isOpen={areaModal.open}
        onClose={() => setAreaModal({
          open: false,
          isEdit: false,
          formData: { name: '' },
          originalId: null
        })}
        onSubmit={handleSaveBusinessArea}
        title={areaModal.isEdit ? "Edit Business Area" : "Add New Business Area"}
        submitText={areaModal.isEdit ? "Update Business Area" : "Add Business Area"}
      >
        <Box mb={2}>
          <TextField
            id="area-name"
            name="name"
            label="Business Area Name"
            required
            value={areaModal.formData.name}
            onChange={(e) => setAreaModal(prev => ({
              ...prev,
              formData: { ...prev.formData, name: e.target.value }
            }))}
            placeholder="Enter business area name"
            autoComplete="off"
            fullWidth
            variant="outlined"
          />
        </Box>
      </Modal>

      <ConfirmationModal
        isOpen={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, businessArea: null })}
        onConfirm={confirmDelete}
        title="Delete Business Area"
        message={`Are you sure you want to delete ${deleteModal.businessArea?.name}?`}
        actionButtonText="Delete"
      />
    </Container>
  );
};

export default BusinessAreasConfiguration; 